import { useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  Button,
  ErrorState,
  Icon,
  IconButton,
  Input,
  Loading,
  Modal,
  Select
} from '@/components/ui'
import { count, money } from '@/lib/format'
import { Cur } from '@/lib/currency'

// Редактор техкарты блюда (§1/§6 плана ресторанного режима). Открывается с
// карточки блюда на странице «Меню бара»: состав правится там, где живёт блюдо.
// Состав отправляется целиком (PUT) — сервер заменяет техкарту и пересчитывает
// себестоимость блюда.

const UNIT_LABEL: Record<string, string> = { pcs: 'шт', kg: 'кг', g: 'г', l: 'л', ml: 'мл' }

interface StockItemRef {
  id: string
  name: string
  unit: string
  costPrice: number
  isStopped: boolean
}

interface RecipeLine {
  id: string
  stockItemId: string
  stockItemName: string
  qtyPerPortion: number
  unit: string
  costPrice: number
  isStopped: boolean
}

interface Recipe {
  menuItemId: string
  lines: RecipeLine[]
  costPrice: number
  modifiers: { id: string; name: string; type: string; priceDelta: number }[]
}

interface DraftLine {
  stockItemId: string
  qtyPerPortion: string
}

export function RecipeEditor({
  menuItemId,
  menuItemName,
  menuItemPrice,
  onClose,
  onSaved
}: {
  menuItemId: string
  menuItemName: string
  menuItemPrice: number
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const query = useConfigQuery()
  const recipe = useFetch<Recipe>(`/v1/warehouse/recipes/${menuItemId}`, query, [menuItemId])
  const stock = useFetch<StockItemRef[]>('/v1/warehouse/items', query, [query?.clubId])
  const [lines, setLines] = useState<DraftLine[] | null>(null)
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const items = stock.data ?? []
  // Первичная инициализация формы из загруженной техкарты.
  const draft: DraftLine[] =
    lines ??
    (recipe.data
      ? recipe.data.lines.map((l) => ({
          stockItemId: l.stockItemId,
          qtyPerPortion: String(l.qtyPerPortion)
        }))
      : [])

  const setLine = (idx: number, patch: Partial<DraftLine>): void =>
    setLines(draft.map((l, i) => (i === idx ? { ...l, ...patch } : l)))

  const itemById = (id: string): StockItemRef | undefined => items.find((i) => i.id === id)

  // Себестоимость по текущим ценам склада — то же, что посчитает сервер.
  const cost = draft.reduce((sum, l) => {
    const it = itemById(l.stockItemId)
    const qty = Number(l.qtyPerPortion)
    if (!it || !Number.isFinite(qty)) return sum
    return sum + qty * it.costPrice
  }, 0)
  const margin = menuItemPrice - cost

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const prepared: { stockItemId: string; qtyPerPortion: number }[] = []
    const seen = new Set<string>()
    for (const l of draft) {
      if (!l.stockItemId) return setValidation('В строке не выбран ингредиент.')
      if (seen.has(l.stockItemId)) {
        return setValidation(
          `Ингредиент «${itemById(l.stockItemId)?.name ?? ''}» указан дважды — объедините строки.`
        )
      }
      seen.add(l.stockItemId)
      const qty = Number(l.qtyPerPortion)
      if (!Number.isFinite(qty) || qty <= 0) {
        return setValidation(
          `«${itemById(l.stockItemId)?.name ?? ''}»: расход на порцию должен быть больше нуля.`
        )
      }
      prepared.push({ stockItemId: l.stockItemId, qtyPerPortion: qty })
    }
    setValidation(null)
    const ok = await save.mutate(() =>
      api.put(`/v1/warehouse/recipes/${menuItemId}`, { lines: prepared }, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={`Техкарта: ${menuItemName}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="recipe-form">
            Сохранить техкарту
          </Button>
        </>
      }
    >
      {(recipe.loading || stock.loading) && <Loading />}
      {recipe.error && <ErrorState message={recipe.error} />}
      {stock.error && <ErrorState message={stock.error} />}

      {!recipe.loading && !stock.loading && (
        <form id="recipe-form" onSubmit={(e) => void onSubmit(e)}>
          {items.length === 0 ? (
            <p className="muted">
              Сначала заведите номенклатуру склада — в разделе «Склад бара и кухни».
            </p>
          ) : (
            <>
              <div className="table-wrap">
                <table className="rtable">
                  <thead>
                    <tr>
                      <th>Ингредиент</th>
                      <th className="num" style={{ width: 140 }}>
                        Расход на порцию
                      </th>
                      <th className="num" style={{ width: 130 }}>
                        Себестоимость
                      </th>
                      <th style={{ width: 48 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {draft.map((l, idx) => {
                      const it = itemById(l.stockItemId)
                      const qty = Number(l.qtyPerPortion)
                      const lineCost = it && Number.isFinite(qty) ? qty * it.costPrice : 0
                      return (
                        <tr key={idx}>
                          <td>
                            <Select
                              value={l.stockItemId}
                              onChange={(e) => setLine(idx, { stockItemId: e.target.value })}
                            >
                              <option value="">— выберите —</option>
                              {items.map((i) => (
                                <option key={i.id} value={i.id}>
                                  {i.name} ({UNIT_LABEL[i.unit] ?? i.unit})
                                  {i.isStopped ? ' · стоп' : ''}
                                </option>
                              ))}
                            </Select>
                          </td>
                          <td className="num">
                            <Input
                              type="number"
                              step="0.0001"
                              min="0"
                              value={l.qtyPerPortion}
                              onChange={(e) => setLine(idx, { qtyPerPortion: e.target.value })}
                            />
                          </td>
                          <td className="num">
                            {money(lineCost)} <Cur />
                          </td>
                          <td>
                            <IconButton
                              danger
                              type="button"
                              title="Убрать ингредиент"
                              onClick={() => setLines(draft.filter((_, i) => i !== idx))}
                            >
                              {Icon.trash}
                            </IconButton>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <Button
                variant="ghost"
                size="sm"
                type="button"
                style={{ marginTop: 8 }}
                onClick={() => setLines([...draft, { stockItemId: '', qtyPerPortion: '' }])}
              >
                {Icon.plus}
                Добавить ингредиент
              </Button>

              <p className="muted" style={{ marginTop: 12 }}>
                Себестоимость порции: {money(cost)} <Cur /> · цена блюда {money(menuItemPrice)}{' '}
                <Cur /> · маржа{' '}
                <span style={margin < 0 ? { color: 'var(--red)' } : undefined}>
                  {money(margin)} <Cur />
                </span>
                {menuItemPrice > 0 &&
                  cost > 0 &&
                  ` (${count(Math.round((margin / menuItemPrice) * 100))}%)`}
              </p>
              <p className="muted">
                Пустая техкарта означает, что блюдо не списывает склад и его себестоимость считается
                нулевой. Стоп любого ингредиента делает блюдо недоступным.
              </p>
            </>
          )}
          {validation && <div className="form-error">{validation}</div>}
          {save.error && <div className="form-error">{save.error}</div>}
        </form>
      )}
    </Modal>
  )
}
